import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Checkbox        from 'app/components/common/checkbox';
import ClientTimestamp from 'app/components/common/client-timestamp';
import Icon            from 'app/components/common/icon';
import Link            from 'app/components/common/link';
import PrintBtn        from 'app/components/common/print-btn';
import RteViewer       from 'app/components/common/rich-text/viewer';
import SortableList    from 'app/components/common/sortable-list';
import Form            from 'app/components/forms/form';
import Bar             from 'app/components/publish-status/bar';
import GrantSysDuck    from 'app/ducks/grants-system';
import helpers         from 'app/helpers/grant-system';
import utils           from 'app/helpers/utils';
import history         from 'app/history';
import paths           from 'app/paths';
import EntitiesSlx     from 'app/selectors/entities';

const Steps = {
  START: 'start',
  REVIEW: 'review',
};

const getNextStep = (props) => {
  const {program, app, editMode, previewMode} = props;
  if (!app || !program) return Steps.START;
  if (editMode || previewMode) return Steps.START;
  const submittedFormIds = (app.formSubmissions || []).map(fsub => fsub.formId);
  const formId = program.formIds.find(id => !submittedFormIds.includes(id));
  return formId || Steps.REVIEW;
};

class Progress extends React.PureComponent {
  render() {
    const {percent} = this.props;
    // const percent = Math.round(Math.random() * 100);
    const showProgress = (percent > 0) && (percent < 100);
    if (percent >= 100) return <Icon.CheckCircle1 className="gapply-prog-complete" />;
    return (
      <svg viewBox="0 0 24 24" className={`gapply-prog`}>
        <circle cx="12" cy="12" r="11" strokeDasharray={[100, 100]} pathLength={100} stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" className="gapply-prog-circle1" />
        {showProgress && (
          <circle cx="12" cy="12" r="11" strokeDasharray={[percent, 100]} pathLength={100} stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" className="gapply-prog-circle2" />
        )}
      </svg>
    );
  }
}

class GrantProgramApplicationForm extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      hasAck: false,
      currentStep: getNextStep(props),
    };

    this.renderCount = 0;

    this.getSubmissionAttrs = this.getSubmissionAttrs.bind(this);
    this.onChangeAck = this.onChangeAck.bind(this);
    this.onClickStart = this.onClickStart.bind(this);
    this.onClickSubmit = this.onClickSubmit.bind(this);
    this.onClickFormBack = this.onClickFormBack.bind(this);
    this.onAfterFormSubmit = this.onAfterFormSubmit.bind(this);
    this.renderFormTab = this.renderFormTab.bind(this);
    this.onSortForms = this.onSortForms.bind(this);
    this.onClickAddForm = this.onClickAddForm.bind(this);
  }

  componentDidMount() {
    this.props.onChangeStep(this.state.currentStep);
  }

  get forms() {
    const {formMap, program, editMode} = this.props;
    // when in edit mode, the form object may be embedded in program.formMap
    const combinedMap = editMode
      ? {...formMap, ...(program.formMap || {})}
      : formMap;
    return program.formIds.map(id => combinedMap[id]).filter(f => f);
  }

  get progress() {
    return helpers.getAppProgress(this.forms, this.props.app?.formSubmissions || []);
  }

  get isSubmitted() {
    return !!this.props.app?.submittedTs;
  }

  get isProgramActive() {
    return helpers.isActiveAndOpen(this.props.program);
  }

  get canSaveForms() {
    const {program, app, editMode} = this.props;
    if (editMode) return false;
    if (!program || !app) return false;
    if (!this.isProgramActive) return false;
    if (this.isSubmitted) return false;
    return true;
  }

  set currentStep(step) {
    this.setState({currentStep: step});
    this.props.onChangeStep(step);
  }

  onChangeAck(event) {
    const hasAck = event.target.checked;
    this.setState({hasAck});
  }

  onClickStep(step) {
    this.currentStep = step;
  }

  goToNextStep(afterFormId) {
    const forms = this.forms;
    const afterIndex = forms.findIndex(f => f.id === afterFormId);
    const nextForm = forms[afterIndex + 1];
    const nextStep = nextForm ? nextForm.id : getNextStep(this.props);
    this.currentStep = nextStep;
  }

  onClickForm(form, event) {
    event.preventDefault();
    this.currentStep = form.id;
  }
  onSortForms(forms) {
    this.props.onSortForms && this.props.onSortForms(forms);
  }
  onClickAddForm() {
    this.props.onClickAddForm && this.props.onClickAddForm();
  }

  async onClickStart() {
    if (this.props.editMode) return;
    const {grantApp} = await this.props.createApp();
    const path = paths.grantApp(grantApp.id);
    history.push(path);
  }

  onClickSubmit() {
    if (this.props.editMode) return;
    this.props.submitApp();
  }

  onClickFormBack() {
    console.log('---- onClickFormBack');
  }
  async onAfterFormSubmit(form, formSubmission) {
    await this.props.reloadApp();
    this.goToNextStep(form.id);
  }

  getSubmissionAttrs() {
    const {app} = this.props;
    return app ? {grantAppId: app.id} : {};
  }

  renderStatus() {
    const {editMode, program, app} = this.props;
    if (editMode) return null;
    const logoUrl = program.company?.logoUrl;

    return (
      <div className="widther">
        <div className="gapply-status">
          <div className="gapply-status-row">
            {!!logoUrl && <img className="gapply-status-logo" src={logoUrl} alt={program.company.name} />}
            <Link href={paths.grantProgram(program.id)} className="gapply-status-program pink-hover">{program.title}</Link>
          </div>
          <div className="gapply-status-title">{app ? 'My Application' : 'Apply'}</div>
        </div>
      </div>
    );
  }

  getActiveClass(step) {
    return this.state.currentStep === step ? 'active' : '';
  }

  renderFormTab(form, index, dnd) {
    const percent = this.progress[form.id].progressPercent || 0;
    const dndClass = dnd?.className || '';
    const dndHandle = dnd?.handleEl || null;
    return (
      <div className={`gapply-steps-step ${this.getActiveClass(form.id)} ${dndClass}`} key={form.id} onClick={this.onClickStep.bind(this, form.id)}>
        <Progress percent={percent} />
        {dndHandle}
        <div className="gapply-steps-step-subtitle">{form.title}</div>
      </div>
    );
  }

  renderTabs() {
    const {editMode, program, app, onSortForms, onClickAddForm} = this.props;

    return (<>
      <div className="gapply-steps-con">
        <div className="gapply-steps">
          <div className={`gapply-steps-step ${this.getActiveClass(Steps.START)}`} onClick={this.onClickStep.bind(this, Steps.START)}>
            <Progress percent={app ? 100 : 0} />
            {/* <div className="gapply-steps-step-title">Start</div> */}
            <div className="gapply-steps-step-subtitle">Start</div>
          </div>
          {!!onSortForms ? (<>
            <SortableList items={this.forms} renderItem={this.renderFormTab} onSort={this.onSortForms} distinctHandle={true} forceUpdate={++this.renderCount} />
          </>) : (<>
            {this.forms.map((form, i) => this.renderFormTab(form, i, null))}
          </>)}
          {!!onClickAddForm && (
            <div className="gapply-steps-step new" onClick={this.onClickAddForm}>
              <Icon.Add className="gapply-steps-step-icon" />
              <div className="gapply-steps-step-subtitle">Form</div>
            </div>
          )}
          <div className={`gapply-steps-step ${this.getActiveClass(Steps.REVIEW)}`} onClick={this.onClickStep.bind(this, Steps.REVIEW)}>
            <Progress percent={!!app?.submittedTs ? 100 : 0} />
            {/* <div className="gapply-steps-step-title">Review</div> */}
            <div className="gapply-steps-step-subtitle">Submit</div>
          </div>
        </div>
      </div>
    </>);
  }

  renderStartScreen() {
    const {currentStep} = this.state;
    const {program, app, editMode, createPending} = this.props;
    if (!program) return null;
    if (currentStep !== Steps.START) return null;
    const ackCbId = `${program.id}-ack-cb`;
    const btnDisabled = (() => {
      if (app) return true;
      if (program.ackText && !this.state.hasAck) return true;
      if (createPending) return true;
      if (!this.isProgramActive) return true;
      return false;
    })();
    const btnLabel = createPending ? 'Starting...' : 'Start Application';
    const hasAck = !!app || this.state.hasAck;

    return (
      <div className="box" style={{padding: 96, margin: '48px auto', width: 800}}>
        <h1 className="bform-h1">{program.title}</h1>
        <RteViewer md={program.entryText} fontSize="large" className="_mt-48 _mb-96" />

        {!!program.ackText && (
          <div className="_flex _align-center _gap-6 _justify-start _mb-12" style={{padding: 24, backgroundColor: '#F0F0F0'}}>
            <Checkbox checked={hasAck} onChange={this.onChangeAck} id={ackCbId} disabled={!!app} />
            <label htmlFor={ackCbId}>{program.ackText}</label>
          </div>
        )}

        {!app && (
          <div className="_flex _justify-end">
            <button className="btn blue" onClick={this.onClickStart} disabled={btnDisabled}>{btnLabel}</button>
          </div>
        )}
      </div>
    );
  }

  renderReviewScreen() {
    const {currentStep} = this.state;
    const {editMode, program, submitPending} = this.props;
    if (currentStep !== Steps.REVIEW) return null;
    if (!editMode) {
      if (this.isSubmitted) return null;
    }
    const progress = this.progress;
    const isComplete = progress.total.isComplete;
    const btnDisabled = (() => {
      if (!isComplete) return true;
      if (submitPending) return true;
      return false;
    })();
    const btnLabel = submitPending ? 'Submitting...' : 'Submit Application';

    return (
      <div className="box" style={{padding: 96, margin: '48px auto', width: 800}}>
        <h1 className="bform-h1">Ready to Submit?</h1>
        <p className="bform-exp">{isComplete ? 'All forms complete!' : 'Some required fields still need answered before submitting.'}</p>
        <div className="gapply-review-forms">
          {this.forms.map((form, i) => {
            const formProgress = progress[form.id];
            return (
              <div className="gapply-review-forms-form" key={form.id}>
                <Progress percent={formProgress.progressPercent} />
                <Link href="#" className="pink-hover" onClick={this.onClickForm.bind(this, form)}>{form.title}</Link>
              </div>
            );
          })}
        </div>

        <RteViewer md={program.submitText} fontSize="large" className="_mt-48" />

        <div className="_flex _justify-center _mt-96">
          <button className="btn blue" onClick={this.onClickSubmit} disabled={btnDisabled}>{btnLabel}</button>
        </div>
      </div>
    );
  }

  renderThankyouScreen() {
    const {currentStep} = this.state;
    const {editMode, program, app} = this.props;
    if (currentStep !== Steps.REVIEW) return null;
    if (!editMode) {
      if (!this.isSubmitted) return null;
    }
    const timestamp = app?.submittedTs || 0;
    return (
      <div className="box" style={{padding: 96, margin: '48px auto', width: 800}}>
        <h1 className="bform-h1">Application Received!</h1>
        <p className="bform-exp _italic _clr-slate">Submitted on <ClientTimestamp timestamp={timestamp} format="MMMM D, YYYY h:mma" showTz /></p>
        <RteViewer md={program.thankyouText} fontSize="large" className="_mt-48" />
      </div>
    );
  }

  render() {
    const {currentStep} = this.state;
    const {program, app, editMode} = this.props;
    if (!program) return null;
    
    return (<>
      <Bar entityType="GrantProgram" subtype="appForm" entity={program} company={program.company} editMode={editMode} disablePublish />
      <div className="_pb-48">
        {this.renderStatus()}
        {this.renderTabs()}
        {this.renderStartScreen()}
        {this.forms.map((form) => {
          if (currentStep !== form.id) return null;
          const submission = app?.formSubmissions?.find(fs => fs.formId === form.id) || null;
          return (
            <Form
              key={form.id}
              form={form}
              editMode={editMode}
              previewMode={!this.canSaveForms}
              submission={submission}
              getSubmissionAttrs={this.getSubmissionAttrs}
              onAfterSubmit={this.onAfterFormSubmit}
              allowPartialSave
              submitLabel="Save and Continue"
              onClickBack={this.onClickFormBack}
              hideLogo
            />
          );
        })}
        {this.renderReviewScreen()}
        {this.renderThankyouScreen()}
      </div>
      <div className="_flex _align-center _justify-center _gap-24 _pb-48">
        {!editMode && <PrintBtn href={paths.grantProgramPrint(program.id)} label="Print application form" />}
      </div>
    </>);
  }

}

GrantProgramApplicationForm.propTypes = {
  program: PropTypes.object,
  app: PropTypes.object,
  editMode: PropTypes.bool,
  onChangeStep: PropTypes.func,
  onSortForms: PropTypes.func, // when present, the tabs become DND-ible
  onClickAddForm: PropTypes.func, // when present, a button to add a new form will is shown among the tabs
};
GrantProgramApplicationForm.defaultProps = {
  editMode: false,
  onChangeStep: utils.noop,
};

const stateToProps = (state) => ({
  formMap: EntitiesSlx.forms(state),
  createPending: GrantSysDuck.Slx.appCreatePending(state),
  submitPending: GrantSysDuck.Slx.appSubmitPending(state),
});
const dispatchToProps = (dispatch, ownProps) => ({
  createApp: () => dispatch(GrantSysDuck.Ax.appsCreate(ownProps.program.id)),
  reloadApp: () => dispatch(GrantSysDuck.Ax.appsGet(ownProps.app?.id)),
  submitApp: () => dispatch(GrantSysDuck.Ax.appsSubmit(ownProps.app?.id)),
});

const ConnectedGrantProgramApplicationForm = connect(stateToProps, dispatchToProps, undefined, {forwardRef: true})(GrantProgramApplicationForm);
ConnectedGrantProgramApplicationForm.Steps = Steps;

export default ConnectedGrantProgramApplicationForm;
